import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';

import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';

import { PortalRenderer } from '@/components/PortalRenderer';
import { Accordion, AccordionContent, AccordionItem, AccordionBasicTrigger } from '@/components/ui/accordion';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConfigStore } from '@/store/ConfigStore';
import { useModalStore } from '@/store/ModalStore';

interface AccordionSectionConfig {
  id: string;
  title: string;
  icon: string;
  content: (clientName: string, setActiveModal: (modal: 'howItWorks' | 'legal' | null) => void) => ReactNode;
}

const accordionSections: AccordionSectionConfig[] = [
  {
    id: 'locations',
    title: 'Start and end locations',
    icon: '/icons/map-pin.svg',
    content: () => (
      <>
        <p>
          Our search uses Google Maps. You can search for any business, landmark, or location, just like you would on
          Google Maps.
        </p>
        <p className="mt-4">
          If the location you are looking for isn't showing up, it's possible that it's outside the area we have air
          quality information for. If this happens, try searching for somewhere else nearby, or click use my location.
        </p>
      </>
    ),
  },
  {
    id: 'transport',
    title: 'Walking or cycling',
    icon: '/icons/person-simple-walk.svg',
    content: () => (
      <>
        <p>Below the search boxes you can change the results to show routes for walking or cycling.</p>
        <p className="mt-4">
          The routes could be different based on the best routes for walking or cycling. For example, following a cycle
          path.
        </p>
      </>
    ),
  },
  {
    id: 'future',
    title: 'Planning a route in the future',
    icon: '/icons/clock.svg',
    content: (clientName) => (
      <>
        <p>Clicking leave now allows you to pick a date and time up to 2 days in the future.</p>
        <p className="mt-4">On phones, this is at the top right of the screen.</p>
        <p className="mt-4">On computers, this is next to the {clientName} logo.</p>
        <p className="mt-4">
          The results are based on forecasted air quality. The closer to the current date and time, the more accurate
          the forecast will be.
        </p>
      </>
    ),
  },
  {
    id: 'navigation',
    title: 'Navigation',
    icon: '/icons/navigation-arrow.svg',
    content: () => (
      <>
        <p>
          Once you have planned your journey you can select your preferred route on the map and navigate by following
          the directions shown.
        </p>
        <p className="mt-4">
          On phones, you can view your location on the map and the turn-by-turn directions above it. You can also open
          the route in Google Maps.
        </p>
        <p className="mt-4">On computers, you can print the directions.</p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Sharing and saving a route',
    icon: '/icons/export.svg',
    content: () => (
      <>
        <p>
          You can share a route by clicking the share icon. You can also save this link to your device to go back to in
          the future.
        </p>
        <p className="mt-4">
          When the link is clicked it will re-check the route and the air quality data. You may find that the best route
          and the amount of exposure has changed from when you last checked.
        </p>
      </>
    ),
  },
  {
    id: 'data',
    title: 'Data we collect',
    icon: '/icons/database.svg',
    content: (clientName, setActiveModal) => (
      <>
        <p>
          We anonymously collect information every time you use {clientName}. This helps us understand how you use it.
          We collect:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-8">
          <li>mode of transport</li>
          <li>results offered</li>
          <li>route start and end points, rounded to the nearest 250 metres squared (m2)</li>
        </ul>
        <p className="mt-4">
          <button
            type="button"
            onClick={() => {
              setActiveModal('legal');
            }}
            className="cursor-pointer text-left text-[var(--blue-one)] underline underline-offset-4 dark:text-white dark:opacity-50 hover:dark:opacity-100"
          >
            You can read more about the data we collect.
          </button>
        </p>
        <p className="mt-4">This data is collected anonymously and will help us to improve the route finder.</p>
      </>
    ),
  },
  {
    id: 'feedback',
    title: 'Feedback and support',
    icon: '/icons/question.svg',
    content: (clientName) => (
      <>
        <p>We hope you have found {clientName} to be useful and easy to use.</p>
        <p className="mt-4">
          If you wish to share your feedback, need further help and support, or you have a problem with the service,
          please contact us at{' '}
          <a href="mailto:smart@westminster.gov.uk" className="text-[var(--blue-one)] underline underline-offset-4">
            smart@westminster.gov.uk
          </a>
          . Please include {clientName} in the subject line.
        </p>
      </>
    ),
  },
];

interface AccordionSectionProps {
  section: AccordionSectionConfig;
  isOpen: boolean;
  onRefSet: (id: string, element: HTMLDivElement | null) => void;
  clientName: string;
  setActiveModal: (modal: 'howItWorks' | 'legal' | null) => void;
}

const AccordionSection = ({ section, isOpen, onRefSet, clientName, setActiveModal }: AccordionSectionProps) => {
  return (
    <AccordionItem
      ref={(el) => onRefSet(section.id, el)}
      value={section.id}
      className="rounded border-b-4 border-[var(--accent-green)] bg-[var(--input)] px-4 dark:bg-gray-800"
    >
      <AccordionBasicTrigger className="flex w-full items-center justify-between gap-4 py-4">
        <div className="flex flex-col items-start gap-2">
          <h4 className="text-left text-lg font-semibold text-[var(--blue-dark)] dark:text-white">{section.title}</h4>
          <div className="flex items-center gap-1 text-[var(--accent-red)] dark:text-[var(--accent-red)]">
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span className="text-sm text-[var(--blue-one)] underline underline-offset-4">
              {isOpen ? 'Hide' : 'Show'}
            </span>
          </div>
        </div>
        <img src={section.icon} alt="" className="h-8 w-8 shrink-0" />
      </AccordionBasicTrigger>
      <AccordionContent className="pb-4 text-lg leading-relaxed text-[var(--gray-one)] dark:text-gray-300">
        {section.content(clientName, setActiveModal)}
      </AccordionContent>
    </AccordionItem>
  );
};

export const HowItWorks = () => {
  const portalSelector = useConfigStore((state) => state.portals?.['how-it-works']);

  return (
    <PortalRenderer portalSelector={portalSelector}>
      <HowItWorksInternal />
    </PortalRenderer>
  );
};

const HowItWorksInternal = () => {
  const { activeModal, setActiveModal } = useModalStore();
  const config = useConfigStore((state) => state.config);
  const clientName = config?.name || 'Clean Air Routes London';
  const isOpen = activeModal === 'howItWorks';
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleRefSet = useCallback((id: string, element: HTMLDivElement | null) => {
    sectionRefs.current[id] = element;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      setAccordionValue(section);
      requestAnimationFrame(() => {
        sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, []);

  const handleAccordionChange = useCallback((value: string | undefined) => {
    setAccordionValue(value);
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set('section', value);
    } else {
      params.delete('section');
    }
    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setActiveModal(open ? 'howItWorks' : null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="cursor-pointer text-left text-lg text-[var(--blue-one)] underline underline-offset-4 dark:text-white dark:opacity-50 hover:dark:opacity-100">
          How it works
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm [&>.close]:hidden">
        <DialogClose className="flex w-full cursor-pointer items-center gap-2 text-left text-sm text-[var(--blue-one)]">
          <ChevronLeft strokeWidth={1} className="-ml-2 h-5" />
          <span className="underline underline-offset-4">Back to map</span>
        </DialogClose>

        <ScrollArea className="h-[80vh] pr-4">
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[var(--gray-one)] dark:text-white">
                About {clientName}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-[var(--gray-one)] dark:text-white">How it works</h3>
              <div className="space-y-4 text-[var(--gray-one)] dark:text-white">
                <p>
                  {clientName} helps you find walking or cycling routes based on the forecasted air quality across
                  Greater London, Surrey and parts of Hertfordshire and Kent. Zoom out the map to see the full area.
                </p>
                <p>
                  Using Google Maps, we compare the routes with forecast air quality data from Cambridge Environmental
                  Research Consultants (CERC).
                </p>
                <p>
                  The lower the level of Nitrogen Dioxide (NO2) and Particulate Matter 2.5 (PM2.5), the better the air
                  quality is. You can learn more about these pollutants on the{' '}
                  <Link
                    target="_blank"
                    href="https://www.who.int/teams/environment-climate-change-and-health/air-quality-and-health/health-impacts/types-of-pollutants"
                  >
                    World Health Organisation (WHO) website
                  </Link>
                  .
                </p>
                <p>
                  There are up to 3 routes to choose from. The best route may change based on the latest data available.
                </p>
                <p>We colour code the routes based on the air quality comparison between them:</p>
                <ul className="list-disc pl-8 text-[var(--gray-one)] dark:text-white">
                  <li>green routes have the best air quality</li>
                  <li>yellow routes have moderate air quality</li>
                  <li>red routes have the poorest air quality</li>
                  <li>
                    blue routes do not have any air quality data. It may be outside the area, or we currently do not
                    have any recent air quality data available
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-[var(--gray-one)] dark:text-white">How to use {clientName}</h3>
              <Accordion
                type="single"
                collapsible
                className="w-full space-y-2"
                value={accordionValue}
                onValueChange={handleAccordionChange}
              >
                {accordionSections.map((section) => (
                  <AccordionSection
                    key={section.id}
                    section={section}
                    isOpen={accordionValue === section.id}
                    onRefSet={handleRefSet}
                    clientName={clientName}
                    setActiveModal={setActiveModal}
                  />
                ))}
              </Accordion>
            </div>

            <div className="mt-8 space-y-2">
              <img src="/wcc-logo.png" alt="Westminster City Council" className="h-12" />
              <p className="text-[var(--gray-one)] dark:text-white">
                {clientName}, a <Link href="https://www.westminster.gov.uk/">Westminster City Council</Link> product.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const Link = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className="text-[var(--blue-one)] underline underline-offset-4 dark:text-white dark:opacity-50 hover:dark:opacity-100"
    target="_blank"
    {...props}
  >
    {props.children}
  </a>
);
